import React from 'react';
import { Image, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../../components/MyAppText';
import { translate } from '../../services/translate';
import Badge from '../../components/Badge';
import { WindowInformation } from '../../services/window-information';

interface Props {
    capture?: string;
    facialSent?: string;
    reference?: string;
    confidence?: string | null;
}

export default function SmartSearchFacialComparer({ capture, reference, facialSent, confidence }: Props) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    function confidenceToText() {
        const confidenceValue = (Number(confidence) * 100);

        if (confidenceValue > 93) {
            return translate('veryHigh');
        }

        if (confidenceValue > 85) {
            return translate('high');
        }

        if (confidenceValue > 70) {
            return translate('medium');
        }

        if (confidenceValue > 50) {
            return translate('low');
        }

        return translate('veryLow');
    }

    function confidenceToColor() {
        const confidenceValue = (Number(confidence) * 100);

        if (confidenceValue > 93) {
            return '#BF4B46';
        }

        if (confidenceValue > 85) {
            return '#A95C18';
        }

        if (confidenceValue > 70) {
            return '#816728';
        }

        if (confidenceValue > 50) {
            return '#00817E';
        }

        return '#4677A8';
    }

    function badgeColor() {
        const confidenceValue = (Number(confidence) * 100);

        if (confidenceValue > 93) {
            return '#BF4B46';
        }

        if (confidenceValue > 85) {
            return '#FFAC63';
        }

        if (confidenceValue > 70) {
            return '#FDDB86';
        }

        if (confidenceValue > 50) {
            return '#92D1CF';
        }

        return '#A4D1FF';
    }

    function badgeTextColor() {
        const confidenceValue = (Number(confidence) * 100);

        if (confidenceValue > 93) {
            return '#FFFFFF';
        }

        if (confidenceValue > 85) {
            return '#6D4000';
        }

        if (confidenceValue > 70) {
            return '#705208';
        }

        if (confidenceValue > 50) {
            return '#175958';
        }

        return '#00324D';
    }

    return (
        <View style={styles.content}>
            <MyAppText style={styles.header}>
                {capture && facialSent
                    ? translate('captureAgainstFacialSent')
                    : capture && reference
                        ? translate('captureAgainstReference')
                        : ''}
            </MyAppText>
            <View style={[windowInfo.isMobile ? styles.bodyMobile : styles.body]}>
                <View style={styles.bodyItem}>
                    <MyAppText style={styles.itemTitle}>{translate('registeredCapture')}</MyAppText>
                    <Image
                        style={styles.imageStyle}
                        source={{ uri: capture }}
                    />
                </View>
                <View style={styles.bodyItem}>
                    <MyAppText style={styles.itemTitle}>{facialSent ? translate('facialSent') : translate('registeredReference')}</MyAppText>
                    <Image
                        style={styles.imageStyle}
                        source={{ uri: facialSent ? facialSent : reference }}
                    />
                </View>
            </View>
            <View style={styles.footer}>
                <MyAppText style={styles.confidence(confidenceToColor())}>{translate('confidence')}: {(Number(confidence) * 100).toFixed(0)}%</MyAppText>
                <Badge
                    mode='ROUNDED'
                    backgroundColor={badgeColor()}
                    textColor={badgeTextColor()}
                >
                    {confidenceToText()}
                </Badge>
            </View>
        </View>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    content: {
        flexDirection: 'column',
        rowGap: 10,
    },
    header: {
        fontSize: 16,
        fontWeight: '600',
        color: theme.colors.secondary['400']
    },
    body: {
        flexDirection: 'row',
        columnGap: 76,
    },
    bodyMobile: {
        flexDirection: 'column',
        rowGap: 16,
    },
    bodyItem: {
        flexDirection: 'column',
        rowGap: 10,
    },
    itemTitle: {
        fontSize: 14,
        fontWeight: '500',
        color: theme.colors.neutral['400']
    },
    imageStyle: {
        width: 152,
        height: 202,
        borderRadius: 8,
        borderWidth: 2,
        borderColor: theme.colors.neutral['200']
    },
    footer: {
        flexDirection: 'row',
        columnGap: 8,
        alignItems: 'center',
    },
    confidence: (color: string) => ({
        fontSize: 14,
        fontWeight: '600',
        color,
    })
}));
